// Foco's signature motion: video resolving out of static, with the cyan/red
// channel split converging into one sharp image. Purely visual; it never
// stands in for real render progress.

const reducedMotion = matchMedia("(prefers-reduced-motion: reduce)");
export const prefersReducedMotion = () => reducedMotion.matches;

const clamp = (v: number, a = 0, b = 1) => Math.min(b, Math.max(a, v));
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const ease = (t: number) => 1 - Math.pow(1 - t, 3);
const smoothstep = (x: number) => {
  const t = clamp(x);
  return t * t * (3 - 2 * t);
};

type Frame = (t: number) => boolean | void;
const frames = new Set<Frame>();
let running = false;
function loop(t: number) {
  for (const f of frames) if (f(t) === false) frames.delete(f);
  if (frames.size) requestAnimationFrame(loop);
  else running = false;
}
export function onFrame(f: Frame) {
  frames.add(f);
  if (!running) {
    running = true;
    requestAnimationFrame(loop);
  }
  return () => frames.delete(f);
}
export function tween(duration: number, fn: (k: number) => void) {
  return new Promise<void>((resolve) => {
    if (prefersReducedMotion()) {
      fn(1);
      resolve();
      return;
    }
    const t0 = performance.now();
    onFrame((t) => {
      const k = clamp((t - t0) / duration);
      fn(k);
      if (k >= 1) {
        resolve();
        return false;
      }
    });
  });
}

let tiles: HTMLCanvasElement[] | null = null;
function noiseTiles() {
  if (tiles) return tiles;
  tiles = [];
  const size = 96;
  for (let k = 0; k < 6; k++) {
    const c = document.createElement("canvas");
    c.width = c.height = size;
    const x = c.getContext("2d")!,
      d = x.createImageData(size, size),
      a = d.data;
    for (let i = 0; i < size * size; i++) {
      a[i * 4] = a[i * 4 + 1] = a[i * 4 + 2] = 255;
      a[i * 4 + 3] = Math.random() > 0.5 ? 95 + Math.random() * 160 : 0;
    }
    x.putImageData(d, 0, 0);
    tiles.push(c);
  }
  return tiles;
}

type Source = HTMLImageElement | HTMLVideoElement;

/** Draws a source through static. The red and cyan channels are offset and
 *  added back together, so as the offset reaches zero the frame is exact. */
export class Resolve {
  private x: CanvasRenderingContext2D;
  private a = document.createElement("canvas");
  private b = document.createElement("canvas");
  private s = document.createElement("canvas");
  private w = 0;
  private h = 0;
  private k = 1;
  private f = 0;
  private last = 0;
  src: Source | null = null;
  quality = 0;
  split = 11;
  noise = 0.9;
  tear = 1;
  dim = 0.5;
  constructor(
    private cv: HTMLCanvasElement,
    private o = { dpr: 1.25, grain: 1.6, fps: 30 },
  ) {
    this.x = cv.getContext("2d")!;
  }
  private fit() {
    const r = this.cv.getBoundingClientRect(),
      d = Math.min(window.devicePixelRatio || 1, this.o.dpr);
    const w = Math.max(2, Math.round(r.width * d)),
      h = Math.max(2, Math.round(r.height * d));
    if (w !== this.w || h !== this.h) {
      this.w = this.cv.width = this.a.width = this.b.width = w;
      this.h = this.cv.height = this.a.height = this.b.height = h;
      this.k = d;
    }
    return r.width > 0;
  }
  /** 0 = pure static, 1 = sharp. */
  set(p: number) {
    this.quality = Math.pow(smoothstep(p), 1.35);
    this.noise = 0.1 + 0.8 * Math.pow(1 - p, 1.1);
    this.split = 2.2 + 9 * (1 - p);
    this.tear = Math.pow(1 - p, 2);
    this.dim = 0.5 * (1 - p);
  }
  private paint(ctx: CanvasRenderingContext2D, w: number, h: number) {
    const s = this.src;
    if (!s) return;
    const sw = "videoWidth" in s ? s.videoWidth : s.naturalWidth,
      sh = "videoHeight" in s ? s.videoHeight : s.naturalHeight;
    if (!sw || !sh) return;
    const r = Math.max(w / sw, h / sh);
    ctx.drawImage(s, (w - sw * r) / 2, (h - sh * r) / 2, sw * r, sh * r);
  }
  draw(t = 0, force = false) {
    if (!force && t && t - this.last < 1000 / this.o.fps) return;
    this.last = t || performance.now();
    if (!this.fit()) return;
    const { w, h, x } = this,
      a = this.a.getContext("2d")!,
      b = this.b.getContext("2d")!;
    a.globalCompositeOperation = "source-over";
    a.fillStyle = "#000";
    a.fillRect(0, 0, w, h);
    if (this.quality < 0.985) {
      const f = 0.025 + 0.975 * this.quality,
        sw = Math.max(4, Math.round(w * f)),
        sh = Math.max(6, Math.round(h * f));
      if (this.s.width !== sw || this.s.height !== sh) {
        this.s.width = sw;
        this.s.height = sh;
      }
      const sc = this.s.getContext("2d")!;
      sc.fillStyle = "#000";
      sc.fillRect(0, 0, sw, sh);
      this.paint(sc, sw, sh);
      a.imageSmoothingEnabled = true;
      a.drawImage(this.s, 0, 0, w, h);
    } else this.paint(a, w, h);
    if (this.dim > 0.01) {
      a.fillStyle = `rgba(0,0,0,${this.dim})`;
      a.fillRect(0, 0, w, h);
    }
    const d = this.split * this.k;
    x.globalCompositeOperation = "source-over";
    x.globalAlpha = 1;
    if (Math.abs(d) < 0.35) x.drawImage(this.a, 0, 0);
    else {
      b.globalCompositeOperation = "source-over";
      b.drawImage(this.a, 0, 0);
      b.globalCompositeOperation = "multiply";
      b.fillStyle = "#ff0000";
      b.fillRect(0, 0, w, h);
      x.fillStyle = "#000";
      x.fillRect(0, 0, w, h);
      x.drawImage(this.b, d, d);
      b.globalCompositeOperation = "source-over";
      b.drawImage(this.a, 0, 0);
      b.globalCompositeOperation = "multiply";
      b.fillStyle = "#00ffff";
      b.fillRect(0, 0, w, h);
      x.globalCompositeOperation = "lighter";
      x.drawImage(this.b, -d, -d);
      x.globalCompositeOperation = "source-over";
    }
    if (this.tear > 0.03) {
      const n = Math.round(Math.random() * 3 * this.tear + this.tear);
      for (let i = 0; i < n; i++) {
        const y = Math.random() * h,
          bh = (2 + Math.random() * 14) * this.k,
          dx = (Math.random() - 0.5) * 44 * this.tear * this.k;
        x.drawImage(this.cv, 0, y, w, bh, dx, y, w, bh);
      }
    }
    if (this.noise > 0.006) {
      const tile = noiseTiles()[this.f++ % 6],
        pattern = x.createPattern(tile, "repeat")!,
        g = this.o.grain * this.k;
      pattern.setTransform?.(
        new DOMMatrix([g, 0, 0, g, Math.random() * 96 * g, Math.random() * 96 * g]),
      );
      x.imageSmoothingEnabled = false;
      x.globalAlpha = this.noise;
      x.fillStyle = pattern;
      x.fillRect(0, 0, w, h);
      x.globalAlpha = 1;
      x.imageSmoothingEnabled = true;
    }
  }
  /** The ready moment: the split springs shut and the static drains. */
  lock(duration = 720) {
    const s0 = Math.max(this.split, 3),
      n0 = this.noise,
      q0 = this.quality;
    return tween(duration, (k) => {
      this.quality = lerp(q0, 1, ease(Math.min(1, k * 1.6)));
      this.dim = 0;
      this.tear = 0;
      this.split = s0 * Math.exp(-5.5 * k) * Math.cos(k * 8.5);
      this.noise = n0 * (1 - ease(k));
      this.draw(0, true);
    });
  }
}

/** Guide rings around an anchor; pulses only while something is live. */
export class RingField {
  private x: CanvasRenderingContext2D;
  private events: { kind: "pulse" | "out" | "in"; t0: number; duration: number }[] = [];
  private on = false;
  private W = 0;
  private H = 0;
  private d = 1;
  private cx = 0;
  private cy = 0;
  private r0 = 0;
  private ro: ResizeObserver;
  constructor(
    private cv: HTMLCanvasElement,
    private anchor: HTMLElement,
    private o = { alpha: 0.075, step: 1.6, first: 1.18 },
  ) {
    this.x = cv.getContext("2d")!;
    this.ro = new ResizeObserver(() => this.fit());
    this.ro.observe(cv);
    this.ro.observe(anchor);
  }
  dispose() {
    this.ro.disconnect();
    this.events = [];
  }
  fit() {
    const r = this.cv.getBoundingClientRect();
    if (!r.width) return;
    this.d = Math.min(2, window.devicePixelRatio || 1);
    this.W = r.width;
    this.H = r.height;
    this.cv.width = Math.round(r.width * this.d);
    this.cv.height = Math.round(r.height * this.d);
    const a = this.anchor.getBoundingClientRect();
    this.cx = a.left - r.left + a.width / 2;
    this.cy = a.top - r.top + a.height / 2;
    this.r0 = (a.height / 2) * 1.04;
    if (!this.on) this.draw(performance.now());
  }
  pulse(on: boolean) {
    this.events = this.events.filter((e) => e.kind !== "pulse");
    if (on && !prefersReducedMotion()) {
      this.events.push({ kind: "pulse", t0: performance.now(), duration: Infinity });
      this.start();
    } else if (!this.on) this.draw(performance.now());
  }
  emit(duration = 1900) {
    if (prefersReducedMotion()) return;
    this.events.push({ kind: "out", t0: performance.now(), duration });
    this.start();
  }
  incoming(duration = 1500) {
    if (prefersReducedMotion()) return;
    this.events.push({ kind: "in", t0: performance.now(), duration });
    this.start();
  }
  private start() {
    if (this.on) return;
    this.on = true;
    onFrame((t) => {
      if (!this.cv.isConnected) {
        this.on = false;
        return false;
      }
      this.draw(t);
      if (!this.events.length) {
        this.on = false;
        return false;
      }
    });
  }
  private ring(x: number, y: number, r: number, col: string, a: number, lw: number) {
    if (r <= 0 || a <= 0.003) return;
    const c = this.x;
    c.strokeStyle = `rgba(${col},${a})`;
    c.lineWidth = lw;
    c.beginPath();
    c.arc(x, y, r, 0, Math.PI * 2);
    c.stroke();
  }
  private split(x: number, y: number, r: number, a: number, lw: number, s: number) {
    if (s > 0.2) {
      this.ring(x - s, y - s, r, "37,244,238", a, lw);
      this.ring(x + s, y + s, r, "254,44,85", a, lw);
    }
    this.ring(x, y, r, "255,255,255", a * (s > 0.2 ? 0.75 : 1), lw);
  }
  private draw(t: number) {
    if (!this.W) return;
    const { x, d, cx, cy, r0 } = this;
    x.setTransform(d, 0, 0, d, 0, 0);
    x.clearRect(0, 0, this.W, this.H);
    const maxR = Math.hypot(Math.max(cx, this.W - cx), Math.max(cy, this.H - cy));
    for (let r = r0 * this.o.first; r < maxR; r *= this.o.step)
      this.ring(cx, cy, r, "255,255,255", this.o.alpha, 1);
    this.events = this.events.filter((e) => t - e.t0 < e.duration);
    for (const e of this.events) {
      const k = (t - e.t0) / e.duration;
      if (e.kind === "in")
        for (let i = 0; i < 3; i++) {
          const kk = clamp((k - i * 0.16) / 0.68);
          if (kk <= 0 || kk >= 1) continue;
          this.split(cx, cy, lerp(maxR * 0.92, r0, ease(kk)), 0.55 * Math.sin(Math.PI * kk), 1.4, 7 * (1 - kk));
        }
      else if (e.kind === "out")
        this.split(cx, cy, lerp(r0, maxR, ease(k)), 0.85 * (1 - k), 1.8 - k, 1.2 * (1 - k));
      else {
        // While work runs: two slow, soft swells, half a period apart.
        const period = 9000,
          elapsed = t - e.t0;
        for (let j = 0; j < 2; j++) {
          const kk = ((elapsed + (j * period) / 2) % period) / period;
          this.ring(cx, cy, lerp(r0 * 1.03, r0 * 2.1, kk), "255,255,255", 0.13 * Math.sin(Math.PI * kk), 1);
        }
      }
    }
  }
}
